import { Link } from "@shared/client/navigation";
import { Eye, Search } from "lucide-react";
import { PanelLink, StatGroup } from "@shared/components/blocks";
import { Alert } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { StatusBadge } from "@/components/status-badge";
import { WatchCard } from "@/components/watch-card";
import { PipelineBar } from "@/components/dashboard/pipeline-bar";
import { StatTile } from "@/components/dashboard/stat-tile";
import { RelativeTime } from "@/components/relative-time";
import { WINDOW_DAYS, type Trend } from "@shared/lib/dashboard-metrics";
import type { LeadsSnapshot } from "@shared/lib/leads";
import { useT } from "@shared/client/locale";

/**
 * Le tableau de bord, sans ses requêtes : la page (app/(product)/app/page.tsx)
 * rassemble les données, ce composant les met en page. La séparation permet
 * aussi de le monter avec des données factices pour une vérification visuelle.
 */

type RunReport = { offer_name: string | null; opportunities?: unknown };

export type DashboardRun = {
  id: string;
  product_url: string;
  status: string;
  source: string | null;
  created_at: string;
  started_at?: string | null;
  reports: RunReport | RunReport[] | null;
};

function reportOf(run: DashboardRun) {
  return Array.isArray(run.reports) ? run.reports[0] : run.reports;
}

/** Rendement d'un passage : ce qu'on veut savoir avant de rouvrir un rapport. */
function yieldOf(run: DashboardRun) {
  const opportunities = reportOf(run)?.opportunities;
  return Array.isArray(opportunities) ? opportunities.length : null;
}

function hostOf(url: string) {
  try {
    return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).host;
  } catch {
    return url;
  }
}

export function DashboardView({
  view,
  watch,
  runTrend,
  opportunityTrend,
  leadTrend,
  sourceCount,
  pipeline,
  runs,
}: {
  /** Espace client consulté en lecture seule par un administrateur. */
  view: { clientName: string } | null;
  /** Carte de veille : absente en lecture seule. */
  watch: React.ComponentProps<typeof WatchCard> | null;
  runTrend: Trend;
  opportunityTrend: Trend;
  leadTrend: Trend;
  sourceCount: number;
  pipeline: LeadsSnapshot | null;
  runs: DashboardRun[];
}) {
  const copy = useT().dashboard;
  return (
    <div className="grid gap-12">
      {view && (
        <Alert className="flex items-center gap-2.5" role="status">
          <Eye aria-hidden="true" className="size-4 text-muted-foreground" />
          <p className="m-0 text-[13px]">
            {copy.readOnly.before}
            <strong className="font-medium">{view.clientName}</strong>
            {copy.readOnly.after}
          </p>
        </Alert>
      )}

      {/* Pas d'en-tête éditorial : la carte de veille porte déjà l'état du
          cycle (première analyse, bilan du dernier passage, prochain passage). */}
      <h1 className="sr-only">{copy.page.heading}</h1>

      {watch && <WatchCard {...watch} />}

      <section aria-labelledby="activity-title" className="grid gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="m-0 text-[14px] font-semibold" id="activity-title">
            {copy.activity.title(WINDOW_DAYS)}
          </h2>
          <span className="text-[12px] text-subtle-foreground">
            {copy.activity.comparison(WINDOW_DAYS)}
          </span>
        </div>
        <StatGroup columns={3}>
          <StatTile
            caption={runTrend.value > 0 ? copy.activity.passesKept : copy.activity.passesEmpty}
            label={copy.activity.passes}
            trend={runTrend}
          />
          <StatTile
            caption={sourceCount > 0 ? copy.activity.sources(sourceCount) : copy.activity.sourcesEmpty}
            label={copy.activity.opportunities}
            trend={opportunityTrend}
          />
          <StatTile
            caption={pipeline ? copy.activity.pipelineTotal(pipeline.total) : copy.activity.personalOnly}
            label={copy.activity.leads}
            trend={leadTrend}
          />
        </StatGroup>
      </section>

      <div className="grid items-start gap-x-12 gap-y-10 lg:grid-cols-[1.4fr_1fr]">
        <section aria-labelledby="runs-title" className="grid gap-3">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="m-0 text-[14px] font-semibold" id="runs-title">{copy.runs.title}</h2>
            <PanelLink href="/app/reports">{copy.runs.history}</PanelLink>
          </div>
          {runs.length === 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3 border-y py-4">
              <p className="m-0 text-[13px] text-muted-foreground">{copy.runs.empty}</p>
              {!view && (
                <Button asChild size="sm" variant="outline">
                  <Link href="#watch"><Search aria-hidden="true" /> {copy.runs.launchFirst}</Link>
                </Button>
              )}
            </div>
          ) : (
            <ul className="m-0 list-none border-y p-0">
              {runs.map((run) => {
                const label = reportOf(run)?.offer_name || hostOf(run.product_url);
                const found = yieldOf(run);
                const meta = (
                  <>
                    <span className="grid min-w-0 gap-0.5">
                      <strong className="truncate text-[13.5px] font-medium">{label}</strong>
                      <small className="truncate text-[12.5px] text-muted-foreground">
                        {run.status !== "completed"
                          ? hostOf(run.product_url)
                          : found === null
                            ? copy.runs.noOpportunity
                            : copy.runs.opportunities(found)}
                        {" · "}
                        {run.source === "radar" ? copy.runs.sources.radar : copy.runs.sources.manual}
                      </small>
                    </span>
                    <span className="inline-flex flex-none items-center gap-5">
                      <StatusBadge status={run.status} />
                      <RelativeTime className="w-[96px] text-right text-[12.5px] text-muted-foreground" value={run.created_at} />
                    </span>
                  </>
                );
                return (
                  <li className="border-t first:border-t-0" key={run.id}>
                    {run.status === "completed" ? (
                      <Link
                        className="-mx-2 flex items-center justify-between gap-4 rounded-md px-2 py-3 hover:bg-muted/70"
                        href={`/app/reports/${run.id}`}
                      >
                        {meta}
                      </Link>
                    ) : (
                      <span className="flex items-center justify-between gap-4 py-3">{meta}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section aria-labelledby="pipeline-title" className="grid gap-3">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="m-0 text-[14px] font-semibold" id="pipeline-title">{copy.pipeline.title}</h2>
            <PanelLink href="/app/leads">{copy.pipeline.open}</PanelLink>
          </div>
          <div className="border-y py-4">
            {pipeline ? (
              <PipelineBar counts={pipeline.counts} total={pipeline.total} />
            ) : (
              <p className="m-0 text-[13px] leading-relaxed text-muted-foreground">
                {copy.pipeline.personalOnly}
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
